import React, { useReducer, useEffect } from "react";
import { get, set } from "../utilities/localStorage";
import localStorageKeys from "../constants/localStorageKeys";
import packageJson from "../../package.json";
import guid from "../utilities/guid";
import { LatLng } from "leaflet";

/**
 * Reducer
 */

enum AuthActionTypes {
  SET_USER = "SET_USER",
  CLEAR_USER = "CLEAR_USER",
  SET_USER_LOCATION = "SET_USER_LOCATION",
  SET_USER_LIKES = "SET_USER_LIKES",
  ADD_LIKE = "ADD_LIKE",
  REMOVE_LIKE = "REMOVE_LIKE",
}

export type AuthState = {
  userId: string;
  userLocation?: LatLng;
  userLikes?: string[];
  userName?: string | null;
  userEmail?: string | null;
};

type AuthActionPayload = {
  id?: string;
  userLikes?: string[];
  userName?: string | null;
  userEmail?: string | null;
  userLocation?: LatLng;
};

type AuthAction = {
  type: AuthActionTypes;
  payload?: AuthActionPayload;
};

/**
 * Reducer
 */

export const authReducer = (
  state: AuthState,
  action: AuthAction
): AuthState => {
  switch (action.type) {
    case AuthActionTypes.SET_USER:
      return {
        ...state,
        userName: action.payload?.userName,
        userEmail: action.payload?.userEmail,
      };
    case AuthActionTypes.SET_USER_LOCATION:
      return {
        ...state,
        userLocation: action.payload?.userLocation,
      };
    case AuthActionTypes.SET_USER_LIKES:
      return {
        ...state,
        userLikes: action.payload?.userLikes,
      };
    case AuthActionTypes.ADD_LIKE:
      const likedEntryId = action.payload?.id || "";

      const oldLikes = state.userLikes || [];
      const likesWithNewLike = [...oldLikes, likedEntryId];

      set(localStorageKeys.USER_LIKES, JSON.stringify(likesWithNewLike));

      return {
        ...state,
        userLikes: likesWithNewLike,
      };
    case AuthActionTypes.REMOVE_LIKE:
      const entryId = action.payload?.id || "";

      const userLikes = state.userLikes || [];
      const likesWithoutNewLike = userLikes.filter((id) => id !== entryId);

      set(localStorageKeys.USER_LIKES, JSON.stringify(likesWithoutNewLike));

      return {
        ...state,
        userLikes: likesWithoutNewLike,
      };

    default:
      return state;
  }
};

/**
 * Create context
 */

type ContextProps = {
  state: AuthState;
  setUser: (userName: string, userEmail?: string) => void;
  setUserLocation: (userLocation: LatLng) => void;
  addLike: (id: string) => void;
  removeLike: (id: string) => void;
};

const initialState: ContextProps = {
  state: {
    userId: "",
    userLikes: [],
    userLocation: undefined,
    userName: null,
    userEmail: null,
  },
  setUser: () => {},
  setUserLocation: () => {},
  addLike: () => {},
  removeLike: () => {},
};

const AuthContext = React.createContext<ContextProps>(initialState);

/**
 * Provider
 */

type Props = {
  children: JSX.Element[] | JSX.Element | string;
  isPublicPage?: boolean;
};

export const AuthProvider: React.FC<Props> = ({ children, isPublicPage }) => {
  const [state, dispatch] = useReducer(authReducer, initialState.state);

  // Ensure user id exists
  useEffect(() => {
    let userId = get(localStorageKeys.USER_ID);

    // If no id exists, create one
    if (!userId) {
      userId = guid();
      set(localStorageKeys.USER_ID, userId);
    }
  }, []);

  // Load user name and email if stored
  useEffect(() => {
    const userName = get(localStorageKeys.USER_NAME);
    const userEmail = get(localStorageKeys.USER_EMAIL);

    if (userName || userEmail) {
      dispatch({
        type: AuthActionTypes.SET_USER,
        payload: {
          userName,
          userEmail,
        },
      });
    }
  }, []);

  // Set app version on load
  useEffect(() => {
    const version = get(localStorageKeys.APP_VERSION);
    if (!version) {
      set(localStorageKeys.APP_VERSION, packageJson.version);
    }
  }, []);

  // Load all likes from localstorage
  useEffect(() => {
    const userLikes = get(localStorageKeys.USER_LIKES);

    if (userLikes) {
      dispatch({
        type: AuthActionTypes.SET_USER_LIKES,
        payload: {
          userLikes: JSON.parse(userLikes) as string[],
        },
      });
    }
  }, []);

  const setUser = (userName: string, userEmail?: string) => {
    set(localStorageKeys.USER_NAME, userName);
    if (userEmail) {
      set(localStorageKeys.USER_EMAIL, userEmail);
    }

    dispatch({
      type: AuthActionTypes.SET_USER,
      payload: {
        userName,
        userEmail,
      },
    });
  };

  const setUserLocation = (userLocation: LatLng) => {
    dispatch({
      type: AuthActionTypes.SET_USER_LOCATION,
      payload: {
        userLocation,
      },
    });
  };

  const addLike = (id: string) => {
    dispatch({
      type: AuthActionTypes.ADD_LIKE,
      payload: {
        id,
      },
    });
  };

  const removeLike = (id: string) => {
    dispatch({
      type: AuthActionTypes.REMOVE_LIKE,
      payload: {
        id,
      },
    });
  };

  return (
    <AuthContext.Provider
      value={{
        setUser,
        setUserLocation,
        addLike,
        removeLike,
        state,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
